import { useEffect, useRef, useState } from 'react'
import { Animated, Easing, Keyboard, Platform } from 'react-native'

/**
 * The on-screen keyboard height (0 when hidden), tracked from the global
 * keyboard events. Pass `enabled` to scope it to the screen that needs it: when
 * `false`, no listeners are registered and it always reports 0.
 *
 * This is the ONLY thing that lifts the flow sheet, for native inputs and the
 * Swapped WebView alike: gorhom's own avoidance runs `keyboardBehavior="extend"`
 * under `enableDynamicSizing`, where the max snap point is already the content
 * height — a no-op, verified on device even with a `BottomSheetTextInput`.
 *
 * iOS emits the `Will` events (ahead of the keyboard animation, so the sheet
 * lifts in sync); Android only reliably emits the `Did` events.
 */
export function useKeyboardHeight(enabled: boolean): number {
  return useKeyboardFollow(enabled).height
}

export interface KeyboardFollow {
  /** Where the keyboard is heading, the moment the event says so. */
  height: number
  /**
   * The same height gliding there on the keyboard's own clock. For a footer
   * that rides the keyboard: iOS posts a fresh `WillShow` each time its
   * suggestion strip appears or goes, and a footer that snaps to each new
   * height while the keyboard itself glides is the jolt that reads as broken.
   */
  animated: Animated.Value
}

/** What iOS animates the keyboard over when the event does not say. */
const KEYBOARD_FALLBACK_MS = 250
// The curve iOS moves its keyboard on; a footer on any other lands early or late.
const KEYBOARD_EASING = Easing.bezier(0.38, 0.7, 0.125, 1)

/**
 * {@link useKeyboardHeight}, plus an `Animated.Value` that follows it on the
 * keyboard's curve. Plain `Animated`, not Reanimated: the screens that ride
 * the keyboard already drive their frames with it, and React Native's
 * `LayoutAnimation` — the other way to glide a layout — is not safe beside
 * Reanimated's entering and exiting transitions.
 */
export function useKeyboardFollow(enabled: boolean): KeyboardFollow {
  const [height, setHeight] = useState(0)
  const animated = useRef(new Animated.Value(0)).current
  useEffect(() => {
    if (!enabled) {
      setHeight(0)
      animated.setValue(0)
      return
    }
    const showEvent =
      Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow'
    const hideEvent =
      Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide'
    const follow = (next: number, duration: number) => {
      setHeight(next)
      if (Platform.OS !== 'ios') {
        // Android reports no curve and fires after the keyboard has moved.
        animated.setValue(next)
        return
      }
      Animated.timing(animated, {
        toValue: next,
        duration: duration > 0 ? duration : KEYBOARD_FALLBACK_MS,
        easing: KEYBOARD_EASING,
        // Layout, not transform: padding and `bottom` cannot use the native driver.
        useNativeDriver: false,
      }).start()
    }
    const show = Keyboard.addListener(showEvent, (event) =>
      follow(event.endCoordinates.height, event.duration),
    )
    const hide = Keyboard.addListener(hideEvent, (event) =>
      follow(0, event.duration),
    )
    return () => {
      show.remove()
      hide.remove()
    }
  }, [enabled, animated])
  return { height, animated }
}
